/**
 * ADR-0028 — the words for a SYSTEM ROW, in the reader's language.
 *
 * Every system row the server writes arrives with two halves: an English
 * sentence in `body`, which is what an older client draws, and the facts in
 * `metadata`. Until s164 every client drew the sentence, so a Kiswahili
 * browser read "Ann added Dee" in the middle of a Kiswahili conversation —
 * and on the web it drew it as an ordinary left-hand bubble, with an author
 * and a hover menu, because the shell had no system row at all.
 *
 * Joana does not consume KMP, so this is the TypeScript twin of
 * `SystemNotice` (`mobile/shared/.../domain/SystemNotice.kt`) plus
 * `systemNoticeText` (`Format.kt` on Android, `UIHelpers.swift` on Apple).
 * The four are changed together; the events and the sentences must match, and
 * `system-notice.test.ts` is what says so out loud.
 *
 * The rule the whole file turns on: an event this build has never heard of
 * returns null, and the caller draws the server's English `body`. That is why
 * the server keeps writing one.
 */
import type { Message } from "./types.ts";

/** The shape `translator()` returns: the English text is the key, `%s`
 *  placeholders are filled positionally. */
export type Translate = (key: string, ...args: (string | number)[]) => string;

/** One person a system row names, and the name the SERVER printed for them
 *  when it wrote the row. `name` is null for an account with neither a display
 *  name nor a handle — the stand-in word is the reader's, not the server's. */
export interface NoticeParty {
  user_id: string;
  name: string | null;
}

/** The facts behind a system row's sentence. Field-for-field the KMP
 *  `SystemNotice`; see that file for what each event uses. */
export interface SystemNotice {
  event: string;
  actor: NoticeParty | null;
  targets: NoticeParty[];
  group_name: string | null;
  role: string | null;
  disappear_seconds: number | null;
  removed: boolean;
  edit_info_policy: string | null;
  media: string | null;
  duration_seconds: number | null;
  action: string | null;
  title: string | null;
}

/** Events that ARE system rows but carry their own richer renderer, so a
 *  generic notice must not also claim them. A 1:1 call's verdict depends on
 *  which end is reading it, which no list of names can express. */
const NON_NOTICE_EVENTS = new Set(["call"]);

/**
 * The system events that name NOBODY, so they localise with no names present.
 *
 * Everything else comes from `Messaging.write_system_message/5`, which has
 * carried `actor_name` since s164 — and a row written BEFORE that has the ids
 * and no names at all. Rendering one would replace a correct English sentence
 * ("Ann added Dee") with a localised sentence about nobody ("Mtu fulani
 * amemwongeza mtu fulani"), which is worse than the thing being fixed. Those
 * rows fall back to `body`, exactly as an unknown event does.
 *
 * The test is the KEY's presence and not the value's: `actor_name` present and
 * null is a name the server could not resolve, and each client has its own
 * word for that. Absent is a server that had not learned to send one.
 */
const NAMELESS_EVENTS = new Set(["group_call", "scheduled_call"]);

const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/**
 * Lift a system row's `metadata` into the facts, or null when the message is
 * not one — including a message with no metadata at all, which every build
 * before ADR-0034 wrote for plenty of rows.
 */
export function parseSystemNotice(msg: Message): SystemNotice | null {
  return parseNoticeMeta(msg.metadata);
}

/**
 * The same parse, from a raw `metadata` map.
 *
 * TWO doors carry that map — a full message in a thread, and the chat list's
 * `last_message.system`, which the server lifts out of the same metadata. One
 * parser, because two would be two chances to read one map differently, and
 * the symptom would be a thread and the list above it disagreeing about the
 * same row.
 */
export function parseNoticeMeta(meta: Record<string, unknown> | null | undefined): SystemNotice | null {
  if (!meta || typeof meta !== "object") return null;
  const event = str(meta.event);
  if (event === null || NON_NOTICE_EVENTS.has(event)) return null;
  // A row from before the server carried names cannot be localised without
  // inventing people — see NAMELESS_EVENTS.
  if (!NAMELESS_EVENTS.has(event) && !("actor_name" in meta)) return null;

  const actorId = str(meta.actor_id);
  const ids = Array.isArray(meta.target_ids) ? meta.target_ids : [];
  // Positional: `target_names[i]` names `target_ids[i]`, and a null there is a
  // name the SERVER could not resolve. Read by index rather than zipped, so a
  // short array (an older server) is a missing name, not a missing person.
  const names = Array.isArray(meta.target_names) ? meta.target_names : [];

  return {
    event,
    actor: actorId === null ? null : { user_id: actorId, name: str(meta.actor_name) },
    targets: ids.flatMap((id, i) => {
      const uid = str(id);
      return uid === null ? [] : [{ user_id: uid, name: str(names[i]) }];
    }),
    group_name: str(meta.name),
    role: str(meta.role),
    disappear_seconds: num(meta.disappear_seconds),
    removed: meta.removed === true,
    edit_info_policy: str(meta.edit_info_policy),
    media: str(meta.media),
    duration_seconds: num(meta.duration_seconds),
    action: str(meta.action),
    title: str(meta.title),
  };
}

/**
 * The words for one system row.
 *
 * Falls back to the server's English `body` for an event this build does not
 * know, and to the empty string only when there is neither — a row with
 * nothing to say draws nothing rather than a placeholder.
 */
export function systemRowText(msg: Message, viewerId: string | null, t: Translate): string {
  const notice = parseSystemNotice(msg);
  if (notice === null) return msg.body ?? "";
  return systemNoticeText(notice, viewerId, t) ?? msg.body ?? "";
}

/**
 * One system event as a sentence, or null for an event this build does not
 * know.
 *
 * `viewerId` decides person: "You added Dee" rather than "Ann added Dee" when
 * Ann is the reader. Not decoration — Kiswahili conjugates the verb for its
 * subject, so those are two sentences and not one with a pronoun swapped in.
 */
export function systemNoticeText(
  notice: SystemNotice,
  viewerId: string | null,
  t: Translate,
): string | null {
  const byYou = viewerId !== null && notice.actor?.user_id === viewerId;
  const isTarget = viewerId !== null && notice.targets.some((p) => p.user_id === viewerId);
  // The reader is the ONLY person this was done to — "Ann added you". With
  // several targets the names read better than a pronoun buried in a list.
  const toYou = notice.targets.length === 1 && isTarget;
  const actor = partyName(notice.actor, t);
  const targets = joinNames(notice.targets, t);

  switch (notice.event) {
    case "renamed":
      return byYou
        ? t("You changed the group name to “%s”", notice.group_name ?? "")
        : t("%s changed the group name to “%s”", actor, notice.group_name ?? "");

    case "member_added":
      if (byYou) return t("You added %s", targets);
      if (toYou) return t("%s added you", actor);
      return t("%s added %s", actor, targets);

    case "member_removed":
      if (byYou) return t("You removed %s", targets);
      if (toYou) return t("%s removed you", actor);
      return t("%s removed %s", actor, targets);

    // The actor IS the target here (the server writes `[actor_id]`), so
    // "You left" is the by-you form and there is no to-you form.
    case "member_left":
      return byYou ? t("You left") : t("%s left", actor);

    case "role_changed":
      return roleText(notice.role, actor, targets, byYou, toYou, t);

    case "disappearing_changed": {
      const seconds = notice.disappear_seconds;
      if (seconds === null) {
        return byYou
          ? t("You turned off disappearing messages")
          : t("%s turned off disappearing messages", actor);
      }
      const duration = disappearDurationText(seconds, t);
      return byYou
        ? t("You turned on disappearing messages: %s", duration)
        : t("%s turned on disappearing messages: %s", actor, duration);
    }

    case "icon_changed":
      if (notice.removed) {
        return byYou ? t("You removed the group icon") : t("%s removed the group icon", actor);
      }
      return byYou ? t("You changed the group icon") : t("%s changed the group icon", actor);

    case "description_changed":
      if (notice.removed) {
        return byYou
          ? t("You removed the group description")
          : t("%s removed the group description", actor);
      }
      return byYou
        ? t("You changed the group description")
        : t("%s changed the group description", actor);

    // `members` is the widening; anything else is the server's default of
    // admins-only, which is also what an unrecognised value means.
    case "policy_changed":
      if (notice.edit_info_policy === "members") {
        return byYou
          ? t("You allowed all members to edit the group info")
          : t("%s allowed all members to edit the group info", actor);
      }
      return byYou
        ? t("You restricted editing the group info to admins")
        : t("%s restricted editing the group info to admins", actor);

    // C3: no verdict and no actor — a group call either happened or it did not,
    // and it is read from every seat in the room the same way.
    case "group_call": {
      const video = notice.media?.toLowerCase() === "video";
      const seconds = notice.duration_seconds ?? 0;
      if (seconds > 0) {
        const length = callDuration(seconds);
        return video ? t("Group video call · %s", length) : t("Group call · %s", length);
      }
      return video ? t("Group video call") : t("Group call");
    }

    // C9. The title is the SNAPSHOT the row was written with, which is what
    // this sentence has always shown; the card reads the live event.
    case "scheduled_call":
      return notice.action === "cancelled"
        ? t("Call cancelled: %s", notice.title ?? "")
        : t("Call scheduled: %s", notice.title ?? "");

    case "e2e_enabled":
      return t("From here, messages in this chat are end-to-end encrypted");

    // Addressed to whoever must act. The person named in `targets` is the one
    // without keys, so for them it is an instruction and for everyone else it
    // is an explanation of why the composer is gone.
    case "peer_must_update":
      return isTarget
        ? t("Update WaveTalk to keep chatting here")
        : t("%s must update WaveTalk before anyone can write here", targets);

    case "e2e_peer_ready":
      return byYou
        ? t("You updated WaveTalk. Messages here are end-to-end encrypted.")
        : t("%s updated WaveTalk. Messages here are end-to-end encrypted.", actor);

    // ADR-0018 — the first line in every zone, and (ADR-0037 B) where a zone
    // says ONCE that it is not end-to-end encrypted: there is no standing card.
    case "zone_created":
      return t("%s created the zone “%s”. It is not end-to-end encrypted: WaveTalk's servers can read it so an assistant can take part.", actor, notice.group_name ?? "");

    default:
      return null;
  }
}

function roleText(
  role: string | null,
  actor: string,
  targets: string,
  byYou: boolean,
  toYou: boolean,
  t: Translate,
): string {
  if (role === "admin") {
    if (byYou) return t("You made %s an admin", targets);
    if (toYou) return t("%s made you an admin", actor);
    return t("%s made %s an admin", actor, targets);
  }
  if (role === "moderator") {
    if (byYou) return t("You made %s a moderator", targets);
    if (toYou) return t("%s made you a moderator", actor);
    return t("%s made %s a moderator", actor, targets);
  }
  // Demotion. `member` is the only other value the server writes, and an
  // unknown one means this build is older than the server — in which case
  // "no longer an admin" is the true half of whatever it said.
  if (byYou) return t("You removed %s as an admin", targets);
  if (toYou) return t("%s removed you as an admin", actor);
  return t("%s removed %s as an admin", actor, targets);
}

/** A person named by a system row. The server sends null rather than the
 *  English word "Someone", so the stand-in is in the reader's language. */
function partyName(party: NoticeParty | null, t: Translate): string {
  return party?.name ?? t("Someone");
}

/** "Ann", "Ann and Dee", "Ann, Dee and Joe" — built from the tail so the
 *  separator and the conjunction are each one translatable string. */
function joinNames(parties: NoticeParty[], t: Translate): string {
  if (parties.length === 0) return t("Someone");
  const names = parties.map((p) => partyName(p, t));
  let acc = names[names.length - 1];
  for (let i = names.length - 2; i >= 0; i--) {
    acc = i === names.length - 2 ? t("%s and %s", names[i], acc) : t("%s, %s", names[i], acc);
  }
  return acc;
}

/**
 * A11: the disappearing-message durations, in words.
 *
 * Only the offered set has a name — the server refuses anything else long
 * before a row can be written with it — and an unrecognised value falls back
 * to the raw seconds, the same posture every other "this build is older than
 * the server" branch here takes.
 */
export function disappearDurationText(seconds: number, t: Translate): string {
  if (seconds === 86_400) return t("24 hours");
  if (seconds === 604_800) return t("7 days");
  if (seconds === 7_776_000) return t("90 days");
  return String(seconds);
}

/** `m:ss`, or `h:mm:ss` past an hour — the TypeScript twin of
 *  `Sema.Api.Messaging.format_call_duration/1` and KMP's `formatCallDuration`.
 *  Three surfaces disagreeing about how long a call ran is a bug a person
 *  reports. */
function callDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}
